import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getCmsLocaleFromLanguage } from "../../api/cmsLocale";

const LANGUAGE_QUERY_PARAM = "lang";
const DEFAULT_LANGUAGE = "me";
const ENGLISH_LANGUAGE = "en";

function normalizeUrlLanguage(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (normalized === ENGLISH_LANGUAGE || normalized.startsWith("en-")) {
    return ENGLISH_LANGUAGE;
  }

  if (
    normalized === DEFAULT_LANGUAGE ||
    normalized === "cg" ||
    normalized === "sr" ||
    normalized === "sr-latn" ||
    normalized === "sr-latn-me"
  ) {
    return DEFAULT_LANGUAGE;
  }

  return "";
}

function buildSearch(searchParams) {
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export default function LanguageUrlSync() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const previousLanguageRef = useRef(getCmsLocaleFromLanguage(i18n.language));
  const previousSearchRef = useRef(location.search);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlLanguage = normalizeUrlLanguage(params.get(LANGUAGE_QUERY_PARAM));
    const activeLanguage = getCmsLocaleFromLanguage(i18n.language);
    const previousLanguage = previousLanguageRef.current;
    const previousSearch = previousSearchRef.current;
    const languageChangedInApp = previousLanguage !== activeLanguage;
    const searchChangedInUrl = previousSearch !== location.search;

    previousLanguageRef.current = activeLanguage;
    previousSearchRef.current = location.search;

    // Initial load or manual URL edit: URL wins.
    // In-app language switch: active i18n language wins and URL is rewritten below.
    if (urlLanguage && urlLanguage !== activeLanguage && !languageChangedInApp) {
      i18n.changeLanguage(urlLanguage);
      return;
    }

    if (urlLanguage && urlLanguage !== activeLanguage && searchChangedInUrl && !languageChangedInApp) {
      i18n.changeLanguage(urlLanguage);
      return;
    }

    const nextParams = new URLSearchParams(location.search);

    if (activeLanguage === ENGLISH_LANGUAGE) {
      if (nextParams.get(LANGUAGE_QUERY_PARAM) === ENGLISH_LANGUAGE) return;

      nextParams.set(LANGUAGE_QUERY_PARAM, ENGLISH_LANGUAGE);
      navigate(
        `${location.pathname}${buildSearch(nextParams)}${location.hash || ""}`,
        { replace: true },
      );
      return;
    }

    if (nextParams.has(LANGUAGE_QUERY_PARAM)) {
      nextParams.delete(LANGUAGE_QUERY_PARAM);
      navigate(
        `${location.pathname}${buildSearch(nextParams)}${location.hash || ""}`,
        { replace: true },
      );
    }
  }, [i18n, i18n.language, location.hash, location.pathname, location.search, navigate]);

  return null;
}
